import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import FullWidthButton from "../components/buttons/FullWidthButton";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { useStaticOptions } from "../context/StaticOptionsContext";
import { useInvoiceSave } from "../context/InvoiceSaveContext";

const languages = [
    { label: "Magyar", value: "hu" },
    { label: "Angol", value: "en" },
    { label: "Idegen", value: "foreign" },
];

const invoiceStatusLabels = {
    0: "Tervezet",
    1: "Feldolgozás alatt",
    5: "Kiegyenlítve",
};

const unwrapOption = (item) => {
    if (!item || typeof item !== "object") return item;
    let current = item;
    let guard = 0;
    while (
        current &&
        typeof current === "object" &&
        current.value &&
        typeof current.value === "object" &&
        guard < 5
    ) {
        current = current.value;
        guard += 1;
    }
    return current;
};

const toArray = (maybeCollection) => {
    if (!maybeCollection) return [];
    const base = Array.isArray(maybeCollection) ? maybeCollection : Object.values(maybeCollection);
    return base.map(unwrapOption);
};

const parseNumberInput = (value) => {
    if (value == null || value === "") return null;
    const parsed = Number(String(value).replace(",", "."));
    return Number.isNaN(parsed) ? null : parsed;
};

const formatAmount = (num) =>
    num == null || num === "" ? "" : (Math.round(Number(num) * 100) / 100).toFixed(2);

const normalizeTaxes = (collection) =>
    toArray(collection)
        .map((item, idx) => {
            const percentRaw =
                item?.tax_degreee ??
                item?.tax_rate ??
                item?.degree ??
                item?.percent ??
                item?.value ??
                0;

            const percentNum = Number(percentRaw);
            if (Number.isNaN(percentNum)) return null;

            const percent = percentNum > 1 ? percentNum : percentNum * 100;
            const multiplier = percent / 100;

            return {
                id: String(item?.tax_szam ?? item?.tax_id ?? item?.id ?? `tax-${idx}`),
                multiplier,
                label: item?.tax_name ?? item?.label ?? `${percent.toFixed(2).replace(/\.00$/, "")}%`,
            };
        })
        .filter(Boolean);

const normalizePmodes = (collection) =>
    toArray(collection).map((item, idx) => {
        const number = Number(item?.pmode_szam ?? item?.value ?? idx);
        return {
            id: String(item?.pmode_szam ?? item?.id ?? `pmode-${idx}`),
            value: String(number),
            label: item?.pmode_name ?? item?.label ?? item?.name ?? "Ismeretlen",
            raw: item,
        };
    });

const formatCurrency = (value, currency = "HUF") =>
    value == null
        ? "-"
        : `${Number(value).toLocaleString("hu-HU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })} ${currency}`;

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("hu-HU");
};

const Szamlazas = () => {
    const { order } = useOrder();
    const navigate = useStepNavigate();
    const { staticOptions } = useStaticOptions();
    const { saveInvoice, loading, successMessage } = useInvoiceSave();
    const [printingInvoiceId, setPrintingInvoiceId] = useState(null);

    const taxes = useMemo(() => normalizeTaxes(staticOptions?.taxes), [staticOptions?.taxes]);
    const pmodes = useMemo(() => normalizePmodes(staticOptions?.pmodes), [staticOptions?.pmodes]);

    const [form, setForm] = useState({
        vatId: "",
        netAmount: "",
        grossAmount: "",
        note: "",
        pmode: "",
        deadline: "",
        language: languages[0].value,
        additionalNote: "",
        receiptType: "normal",
        technicalNote: "",
    });

    const getVatMultiplier = useCallback(
        (vatId) => taxes.find((tax) => tax.id === String(vatId))?.multiplier ?? 0,
        [taxes]
    );

    useEffect(() => {
        setForm((prev) => {
            let changed = false;
            const next = { ...prev };
            if (!prev.vatId && taxes.length) {
                next.vatId = taxes[0].id;
                changed = true;
            }
            if (!prev.pmode && pmodes.length) {
                next.pmode = pmodes[0].value;
                changed = true;
            }
            return changed ? next : prev;
        });
    }, [taxes, pmodes]);

    const handleNetBlur = () => {
        const multiplier = getVatMultiplier(form.vatId);
        const numeric = parseNumberInput(form.netAmount);
        if (numeric == null) return;
        setForm((prev) => ({
            ...prev,
            netAmount: formatAmount(numeric),
            grossAmount: formatAmount(numeric * (1 + multiplier)),
        }));
    };

    const handleGrossBlur = () => {
        const multiplier = getVatMultiplier(form.vatId);
        const numeric = parseNumberInput(form.grossAmount);
        if (numeric == null) return;
        setForm((prev) => ({
            ...prev,
            grossAmount: formatAmount(numeric),
            netAmount: formatAmount(multiplier === 0 ? numeric : numeric / (1 + multiplier)),
        }));
    };

    const handleVatChange = (value) => {
        const multiplier = getVatMultiplier(value);
        setForm((prev) => {
            const netNumeric = parseNumberInput(prev.netAmount);
            return {
                ...prev,
                vatId: value,
                grossAmount: netNumeric != null
                    ? formatAmount(netNumeric * (1 + multiplier))
                    : prev.grossAmount,
            };
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "vatId") return handleVatChange(value);
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleReceiptToggle = (type) => {
        setForm((prev) => ({
            ...prev,
            receiptType: type,
            technicalNote: type === "technical" ? prev.technicalNote : "",
        }));
    };

    const handleTechnicalNoteChange = (e) => {
        const value = e.target.value;
        setForm((prev) => ({
            ...prev,
            technicalNote: value,
            receiptType: value.trim().length ? "technical" : "normal",
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.netAmount || !form.grossAmount) {
            console.warn("Hiányzó összeg mezők!");
            return;
        }

        const result = await saveInvoice({
            vatId: form.vatId,
            netAmount: parseNumberInput(form.netAmount),
            grossAmount: parseNumberInput(form.grossAmount),
            note: form.note,
            pmode: form.pmode,
            deadline: form.deadline,
            language: form.language,
            additionalNote: form.additionalNote,
            receiptType: form.receiptType,
            technicalNote: form.technicalNote,
        });

        if (result.success) {
            // form reset sikeres mentés után
            setForm((prev) => ({
                ...prev,
                netAmount: "",
                grossAmount: "",
                note: "",
                additionalNote: "",
                technicalNote: "",
                receiptType: "normal",
            }));
        }
    };

    const handlePrintInvoice = async (invoice) => {
        if (printingInvoiceId) return;
        setPrintingInvoiceId(invoice.szamla_szam);
        try {
            // TODO: riport letöltés / e-mail küldés
        } finally {
            setPrintingInvoiceId(null);
        }
    };

    const handleBackClick = () => navigate(-1);

    const invoices = order?.szamlak ?? [];
    const orderedInvoices = [...invoices].sort(
        (a, b) => (b.szamla_dpaym ?? 0) - (a.szamla_dpaym ?? 0)
    );

    const formatPaymentMethod = (value) => {
        const match = pmodes.find((pm) => pm.value === String(value));
        return match ? match.label : "Ismeretlen";
    };

    return (
        <TablerLayout title="Számlázás" hideCloseButton>
            <div className="row">
                <div className="col-12 mb-4">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Új előlegszámla</h3>
                        </div>
                        <div className="card-body">
                            <form className="row g-3" onSubmit={handleSubmit}>
                                <div className="col-md-4">
                                    <label className="form-label">ÁFA</label>
                                    <select
                                        className="form-select"
                                        name="vatId"
                                        value={form.vatId}
                                        onChange={handleChange}
                                    >
                                        {taxes.map((tax) => (
                                            <option key={tax.id} value={tax.id}>
                                                {tax.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Előleg nettó összege</label>
                                    <input
                                        className="form-control text-end"
                                        name="netAmount"
                                        value={form.netAmount}
                                        onChange={handleChange}
                                        onBlur={handleNetBlur}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Előleg bruttó összege</label>
                                    <input
                                        className="form-control text-end"
                                        name="grossAmount"
                                        value={form.grossAmount}
                                        onChange={handleChange}
                                        onBlur={handleGrossBlur}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Megjegyzés a számlán</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        name="note"
                                        value={form.note}
                                        onChange={handleChange}
                                    ></textarea>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Egyéb megjegyzés</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        name="additionalNote"
                                        value={form.additionalNote}
                                        onChange={handleChange}
                                    ></textarea>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Fizetési mód</label>
                                    <select
                                        className="form-select"
                                        name="pmode"
                                        value={form.pmode}
                                        onChange={handleChange}
                                    >
                                        {pmodes.map((pmode) => (
                                            <option key={pmode.id} value={pmode.value}>
                                                {pmode.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Határidő</label>
                                    <input
                                        className="form-control text-end"
                                        name="deadline"
                                        value={form.deadline}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Nyelv</label>
                                    <select
                                        className="form-select"
                                        name="language"
                                        value={form.language}
                                        onChange={handleChange}
                                    >
                                        {languages.map((lang) => (
                                            <option key={lang.value} value={lang.value}>
                                                {lang.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-12">
                                    <label className="form-label d-block mb-1">Nyugta típusa</label>
                                    <div className="d-flex gap-4">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="receipt-normal"
                                                checked={form.receiptType === "normal"}
                                                onChange={() => handleReceiptToggle("normal")}
                                            />
                                            <label className="form-check-label" htmlFor="receipt-normal">
                                                Normál nyugta
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="receipt-technical"
                                                checked={form.receiptType === "technical"}
                                                onChange={() => handleReceiptToggle("technical")}
                                            />
                                            <label className="form-check-label" htmlFor="receipt-technical">
                                                Technikai nyugta
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <label className="form-label">Technikai megjegyzés</label>
                                    <textarea
                                        className="form-control"
                                        rows="2"
                                        value={form.technicalNote}
                                        onChange={handleTechnicalNoteChange}
                                    ></textarea>
                                </div>
                                <div className="col-12">
                                    <FullWidthButton type="submit" plusClass="btn-success" disabled={loading}>
                                        {loading ? "Mentés..." : "Előlegszámla felvétele"}
                                    </FullWidthButton>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h3 className="card-title mb-0">Előleg- és végszámlák</h3>
                            <span className="text-muted small">{invoices.length} db</span>
                        </div>
                        <div className="card-body p-0">
                            {invoices.length ? (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Számlaszám</th>
                                                <th>Típus</th>
                                                <th>Kiállítás dátuma</th>
                                                <th>Nettó</th>
                                                <th>ÁFA</th>
                                                <th>Bruttó</th>
                                                <th>Fizetési mód</th>
                                                <th>Státusz</th>
                                                <th className="text-end">Műveletek</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderedInvoices.map((invoice) => (
                                                <tr key={invoice.szamla_szam}>
                                                    <td className="fw-semibold">{invoice.szamla_szam}</td>
                                                    <td>
                                                        <span
                                                            className={`badge ${
                                                                invoice.szamla_dpaym === 1
                                                                    ? "bg-warning text-dark"
                                                                    : "bg-success"
                                                            }`}
                                                        >
                                                            {invoice.szamla_dpaym === 1 ? "Előlegszámla" : "Végszámla"}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(invoice.szamla_inv_date)}</td>
                                                    <td>{formatCurrency(invoice.szamla_netto, invoice.szamla_currency)}</td>
                                                    <td>{formatCurrency(invoice.szamla_vat, invoice.szamla_currency)}</td>
                                                    <td>{formatCurrency(invoice.szamla_total, invoice.szamla_currency)}</td>
                                                    <td>{formatPaymentMethod(invoice.szamla_pmode)}</td>
                                                    <td>{invoiceStatusLabels[invoice.szamla_status] ?? `Státusz #${invoice.szamla_status ?? "-"}`}</td>
                                                    <td className="text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePrintInvoice(invoice)}
                                                            disabled={printingInvoiceId === invoice.szamla_szam}
                                                        >
                                                            {printingInvoiceId === invoice.szamla_szam ? (
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-2"
                                                                    role="status"
                                                                    aria-hidden="true"
                                                                ></span>
                                                            ) : (
                                                                <i className="ti ti-printer me-2"></i>
                                                            )}
                                                            Nyomtatás
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-4 text-center text-muted">Még nincs megjeleníthető számla.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
        </TablerLayout>
    );
};

export default Szamlazas;